@if (!liveEnabled()) {
  <p class="gate__blocked" role="alert">The live workflow is not available in this configuration.</p>
} @else if (store.loading()) {
  <p class="pdw-waiting" role="status">Loading workflow...</p>
} @else if (store.signInRedirect()) {
  <div class="pdw-panel" role="status" aria-live="polite">
    <p>Redirecting to sign in. If this page does not redirect, retry.</p>
    <button (click)="store.load(caseId())" type="button" class="gate__btn gate__btn--primary">
      <i class="pi pi-refresh" aria-hidden="true"></i> Retry
    </button>
  </div>
} @else if (store.loadFailed()) {
  <div class="pdw-panel" role="alert">
    <p>{{ store.caseMissing() ? 'This workflow case no longer exists.' : store.loadStatus() === 403 ? 'You do not have access to this workflow.' : 'The workflow could not be loaded. Try again.' }}</p>
    <button (click)="store.load(caseId())" type="button" class="gate__btn gate__btn--primary">
      <i class="pi pi-refresh" aria-hidden="true"></i> Retry
    </button>
  </div>
} @else if (state(); as state) {
  @let detail = view();
  <ng-template #stepPanel>
    @if (store.form(); as form) {
      <app-engine-step-panel
        [form]="form" [fields]="store.fields()" [candidates]="detail.candidates" [initialValues]="detail.initialValues"
        [embedded]="inlineExecution()"
        [actionDescriptionShown]="actionDescriptionShown()"
        [canClaim]="store.canClaim()" [canDecide]="store.canDecide()" [isOwner]="store.isOwner()"
        [ownerName]="ownerName()" [returnToName]="returnName()" [busy]="store.busy()" [commandError]="store.commandError()"
        (decide)="onDecide($event)" (release)="store.release($event)" presentation="uc2" />
    } @else if (store.commandError(); as error) {
      <p class="gate__blocked" role="alert">{{ error }}</p>
    }
  </ng-template>
  <section class="pdw" aria-label="Well exception workflow">
    <header class="pdw-head">
      <div class="pdw-head__main">
        <div class="pdw-head__title-row">
          <span class="pdw-head__kind">Well exception</span>
          <span class="pdw-head__state" [class.pdw-head__state--closed]="!store.isOpen()">{{ store.isOpen() ? (store.form()?.title ?? 'Open') : 'Closed' }}</span>
          @if (state.outcome) { <span class="pdw-head__outcome">{{ labelFor(state.outcome) }}</span> }
        </div>
        <h3 class="pdw-head__title">{{ state.caseNumber }}</h3>
        @if (store.isOpen()) { <p class="pdw-head__owner">{{ ownerName() ?? (state.ownerOid ? 'Another user' : roleLabel(store.stepRole()) + ' queue') }}</p> }
        <p class="pdw-head__sub">Workflow version {{ state.classVersion }}</p>
      </div>
      <dl class="pdw-head__facts">
        <div><dt>Opened</dt><dd>{{ state.openedOnUtc | date:'dd MMM yyyy, HH:mm':'+0100' }} WAT</dd></div>
        @if (state.closedOnUtc) {
          <div><dt>Closed</dt><dd>{{ state.closedOnUtc | date:'dd MMM yyyy, HH:mm':'+0100' }} WAT</dd></div>
        }
      </dl>
    </header>

    <app-uc2-lifecycle-rail [stages]="railStages()" ariaLabel="Case workflow progress" />

    @if (detail.validity || !store.isOpen()) {
      <section class="pdw-panel" [attr.aria-label]="store.isOpen() ? 'Recorded analyst decision' : 'Case Closed'">
        <h4 class="pdw-panel__title">{{ store.isOpen() ? 'Analyst decision' : 'Case Closed' }}</h4>
        <dl class="pdw-record">
          <div><dt>Validity</dt><dd>{{ detail.validity ? labelFor(detail.validity) : 'Not recorded' }}</dd></div>
          @if (detail.assessment) { <div><dt>Assessment</dt><dd>{{ labelFor(detail.assessment) }}</dd></div> }
          @if (detail.dismissReason) { <div><dt>Dismissal reason</dt><dd>{{ detail.dismissReason }}</dd></div> }
          @if (detail.reclassReason) { <div><dt>Reclassification reason</dt><dd>{{ detail.reclassReason }}</dd></div> }
          @if (detail.claimReason) { <div><dt>Cause selection reason</dt><dd>{{ detail.claimReason }}</dd></div> }
          @if (detail.approvalRationale) { <div><dt>Approval rationale</dt><dd>{{ detail.approvalRationale }}</dd></div> }
          @if (!store.isOpen()) {
            @for (action of detail.actions; track action.id) {
              <div><dt>Action ID</dt><dd>{{ action.id }}</dd></div>
              <div><dt>Action Status</dt><dd>{{ labelFor(action.status) }}</dd></div>
              <div><dt>Action taken</dt><dd>{{ action.actionTaken ?? 'Not recorded' }}</dd></div>
              <div><dt>Observation (feedback)</dt><dd>{{ action.observation ?? 'Not recorded' }}</dd></div>
            } @empty {
              <div><dt>Action results</dt><dd>No action results recorded.</dd></div>
            }
          }
        </dl>
      </section>
    }

    @if (!editingCandidate()) {
      <section class="pdw-panel" [attr.aria-label]="candidateHeading()">
        <h4 class="pdw-panel__title">{{ candidateHeading() }}</h4>
        @if (!detail.selectedCandidate) {
          <p class="gate__muted">
            @if (detail.selectedCandidateId) {
              The recorded candidate explanation is unavailable.
            } @else {
              No candidate selection was recorded.
            }
            @if (displayedCandidates().length) {
              Agent-proposed explanations are shown below, not an analyst selection.
            } @else {
              No candidate explanations are available.
            }
          </p>
        }
        @for (candidate of displayedCandidates(); track candidate.id) {
          <div [class.pdw-cand--selected]="!!detail.selectedCandidate && detail.candidates.length > 1" class="pdw-cand">
            <div class="pdw-cand__label pdw-cand__label--readonly">
              @if (detail.candidates.length > 1) {
                <span class="pdw-cand__rank" aria-hidden="true">{{ candidate.rank }}</span>
              }
              <div class="pdw-cand__body">
                <div class="pdw-cand__head">
                  <app-console-summary-markdown [text]="candidate.label" class="pdw-cand__title" />
                  @if (candidate.family) { <span class="pdw-cand__chip">{{ labelFor(candidate.family) }}</span> }
                </div>
                @if (candidate.rationale) { <app-console-summary-markdown [text]="candidate.rationale" class="pdw-cand__rationale" /> }
              </div>
            </div>
            @if (candidate.recommendedActions.length || inlineExecution()) {
              <div class="uc2-candidate-actions">
                <div class="uc2-candidate-actions__info">
                  <h5 class="uc2-candidate-actions__heading"><i class="pi pi-info-circle" aria-hidden="true"></i> Recommended actions</h5>
                  @for (action of candidate.recommendedActions; track action.id) {
                    <app-console-summary-markdown [text]="action.description" class="uc2-candidate-actions__description" />
                  }
                  @if (inlineExecution()) {
                    <ng-container [ngTemplateOutlet]="stepPanel" />
                  }
                </div>
              </div>
            }
          </div>
        }
      </section>
    }

    @if (analystReturn(); as returned) {
      <section class="pdw-panel" aria-label="Returned for analyst rework" role="status">
        <h4 class="pdw-panel__title">Returned for analyst rework</h4>
        <dl class="pdw-record">
          <div><dt>Returned by</dt><dd>{{ returnedBy() }} · Production engineer</dd></div>
          <div>
            <dt>Returned at</dt>
            <dd><time [attr.datetime]="returned.occurredOnUtc">{{ returned.occurredOnUtc | date:'dd MMM yyyy, HH:mm:ss':'+0100' }} WAT</time></dd>
          </div>
          <div><dt>Reason</dt><dd>{{ returned.reason ?? 'Return reason unavailable in the audit.' }}</dd></div>
        </dl>
      </section>
    }

    @if (!inlineExecution()) {
      <ng-container [ngTemplateOutlet]="stepPanel" />
    }

    @if (suggestions().length) {
      <section class="pdw-panel" aria-label="Suggested signals">
        <h4 class="pdw-panel__title">Suggested signals</h4>
        @for (signal of suggestions(); track signal.signalKey) {
          <p>{{ signal.context['well_code'] ?? signal.context['wellCode'] ?? 'Additional signal' }}</p>
          @if (store.isOwner()) {
            <div class="gate__actions">
              <button [disabled]="store.busy()" (click)="store.resolveSuggestion(signal.signalKey, true)" type="button" class="gate__btn gate__btn--primary">Accept signal</button>
              <button [disabled]="store.busy()" (click)="store.resolveSuggestion(signal.signalKey, false)" type="button" class="gate__btn gate__btn--ghost">Reject signal</button>
            </div>
          }
        }
      </section>
    }

    <details class="pdw-more" aria-label="Audit trail">
      <summary class="pdw-more__summary pdw-more__summary--disclosure">Audit trail</summary>
      <div class="pdw-more__body">
        <app-workflow-audit-trail [trail]="auditTrail()" />
      </div>
    </details>

    @if (store.composed()?.graph; as graph) {
      <app-workflow-published-graph [graph]="graph" [nodes]="store.graph()" />
    }
  </section>
}
